import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useState } from 'react';

import { useMarketData } from '@widget/core';
import { Icon } from '@toolbox/components';
import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider/ThemeProvider';
import { AddressOption } from './components/AddressOption/AddressOption';
import { useGooglePlaces } from './hooks/useGooglePlaces';
import { PaperWithBottom } from './components/PaperWithBottom';

import type {
  Location,
  AutocompleteFieldOption,
} from '@toolbox/components/AddressSuggestField/types';
import type { FC, SyntheticEvent } from 'react';

type AddressSuggestFieldProps = {
  label: string;
  initialValue?: string;
  error?: string;
  setAddress: (location: Location) => void;
};

export const AddressSuggestField: FC<AddressSuggestFieldProps> = ({
  label,
  initialValue,
  setAddress,
  error,
}) => {
  const { market } = useMarketData();
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [value, setValue] = useState<AutocompleteFieldOption | string>(initialValue || '');

  const { loading, searchResultOptions, getPlaceDetails } = useGooglePlaces({
    searchValue: inputValue,
    country: market,
  });

  const handleInputChange = (_: SyntheticEvent, value: string) => {
    setInputValue(value);
  };
  const handleOnChange = async (
    _: SyntheticEvent,
    value: NonNullable<AutocompleteFieldOption | string>,
  ) => {
    setValue(value);
    if (typeof value !== 'string') {
      const location = await getPlaceDetails(value.value);
      setAddress(location);
    }
  };

  const getOptionLabel = (option: AutocompleteFieldOption | string) => {
    if (typeof option === 'string') {
      return option;
    }
    return `${option.label} ${option.secondaryLabel}`;
  };

  const isOptionEqualToValue = (option: AutocompleteFieldOption, value: AutocompleteFieldOption) =>
    value && option.value === value.value;

  return (
    <ThemeProvider>
      <FormControl fullWidth>
        <Autocomplete<AutocompleteFieldOption, false, true, true>
          selectOnFocus={false}
          inputValue={inputValue}
          value={value}
          options={searchResultOptions}
          forcePopupIcon={false}
          disableClearable
          ListboxProps={{ style: { maxHeight: 170 } }}
          fullWidth
          loading={loading}
          loadingText="Laden... bitte warten"
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          onInputChange={handleInputChange}
          onChange={handleOnChange}
          filterOptions={(options) => options}
          renderOption={(tagProps, suggestion, state) => (
            <AddressOption
              key={suggestion.value}
              tagProps={tagProps}
              suggestion={suggestion}
              state={state}
            />
          )}
          renderInput={({ InputProps, ...props }) => (
            <TextField
              label={label}
              InputProps={{ ...InputProps, endAdornment: <Icon type="location" /> }}
              error={Boolean(error)}
              helperText={error}
              {...props}
            />
          )}
          PaperComponent={PaperWithBottom}
        />
      </FormControl>
    </ThemeProvider>
  );
};
